/**
 * Geometría de planta (portada del v6, componente 5 de la auditoría, con pruebas).
 * Coordenadas en metros: x a la derecha, z hacia abajo. Rotación r: el eje local +z apunta a
 * (sin r, cos r), es decir, el «frente» del elemento.
 */

import type { Elemento, Punto } from '../dominio/espacio';

export type Vec = readonly [number, number];

export const p = (x: number, z: number): Punto => ({ x, z });

export function distancia(a: Punto, b: Punto): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/** Dirección del frente de un elemento rotado. */
export function frente(rotacion: number): Punto {
  return { x: Math.sin(rotacion), z: Math.cos(rotacion) };
}

/** Coordenadas locales del elemento → planta. */
export function aPlanta(e: Pick<Elemento, 'posicion' | 'rotacion'>, lx: number, lz: number): Punto {
  const c = Math.cos(e.rotacion);
  const s = Math.sin(e.rotacion);
  return { x: e.posicion.x + lx * c + lz * s, z: e.posicion.z - lx * s + lz * c };
}

/** Huella rectangular del elemento en planta (4 esquinas). */
export function huella(e: Pick<Elemento, 'posicion' | 'rotacion' | 'dimensiones'>): Punto[] {
  const w = e.dimensiones.ancho / 2;
  const d = e.dimensiones.fondo / 2;
  return [aPlanta(e, -w, -d), aPlanta(e, w, -d), aPlanta(e, w, d), aPlanta(e, -w, d)];
}

export function puntoEnPoligono(pt: Punto, poligono: readonly Punto[]): boolean {
  let dentro = false;
  for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i++) {
    const a = poligono[i]!;
    const b = poligono[j]!;
    if (a.z > pt.z !== b.z > pt.z && pt.x < ((b.x - a.x) * (pt.z - a.z)) / (b.z - a.z) + a.x)
      dentro = !dentro;
  }
  return dentro;
}

export interface Interseccion {
  t: number;
  u: number;
  p: Punto;
}

/** Intersección de los segmentos ab y cd (con tolerancia en los extremos). */
export function intersectarSegmentos(a: Punto, b: Punto, c: Punto, d: Punto): Interseccion | null {
  const rx = b.x - a.x;
  const rz = b.z - a.z;
  const sx = d.x - c.x;
  const sz = d.z - c.z;
  const den = rx * sz - rz * sx;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((c.x - a.x) * sz - (c.z - a.z) * sx) / den;
  const u = ((c.x - a.x) * rz - (c.z - a.z) * rx) / den;
  if (t < -1e-6 || t > 1 + 1e-6 || u < -1e-6 || u > 1 + 1e-6) return null;
  return { t, u, p: { x: a.x + t * rx, z: a.z + t * rz } };
}

export interface Proyeccion {
  /** Parámetro sobre el segmento (0–1). */
  t: number;
  q: Punto;
  d: number;
}

/** Proyección del punto sobre el segmento ab (recortada a sus extremos). */
export function proyectarEnSegmento(pt: Punto, a: Punto, b: Punto): Proyeccion {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const l2 = dx * dx + dz * dz || 1e-9;
  const t = Math.max(0, Math.min(1, ((pt.x - a.x) * dx + (pt.z - a.z) * dz) / l2));
  const q = { x: a.x + t * dx, z: a.z + t * dz };
  return { t, q, d: Math.hypot(pt.x - q.x, pt.z - q.z) };
}

/** Longitud de una polilínea. */
export function longitud(puntos: readonly Punto[]): number {
  let l = 0;
  for (let i = 1; i < puntos.length; i++) l += distancia(puntos[i - 1]!, puntos[i]!);
  return l;
}

/** Punto a una distancia `d` sobre la polilínea, con el rumbo del tramo (radianes, como rotación). */
export function puntoEnPolilinea(
  puntos: readonly Punto[],
  d: number,
): { punto: Punto; rumbo: number } {
  if (puntos.length === 0) return { punto: { x: 0, z: 0 }, rumbo: 0 };
  if (puntos.length === 1) return { punto: puntos[0]!, rumbo: 0 };
  let resto = Math.max(0, d);
  for (let i = 1; i < puntos.length; i++) {
    const a = puntos[i - 1]!;
    const b = puntos[i]!;
    const l = distancia(a, b);
    if (resto <= l || i === puntos.length - 1) {
      const f = l > 0 ? Math.min(1, resto / l) : 0;
      return {
        punto: { x: a.x + (b.x - a.x) * f, z: a.z + (b.z - a.z) * f },
        rumbo: Math.atan2(b.x - a.x, b.z - a.z),
      };
    }
    resto -= l;
  }
  const a = puntos[puntos.length - 2]!;
  const b = puntos[puntos.length - 1]!;
  return { punto: b, rumbo: Math.atan2(b.x - a.x, b.z - a.z) };
}

/** ¿Se superponen dos polígonos simples (cóncavos o convexos)? Tolerancia en metros. */
export function poligonosSuperpuestos(a: readonly Punto[], b: readonly Punto[]): boolean {
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i]!;
    const a2 = a[(i + 1) % a.length]!;
    for (let j = 0; j < b.length; j++) {
      const hit = intersectarSegmentos(a1, a2, b[j]!, b[(j + 1) % b.length]!);
      if (hit && hit.t > 1e-6 && hit.t < 1 - 1e-6 && hit.u > 1e-6 && hit.u < 1 - 1e-6) return true;
    }
  }
  const centro = (pol: readonly Punto[]) => ({
    x: pol.reduce((s, q) => s + q.x, 0) / pol.length,
    z: pol.reduce((s, q) => s + q.z, 0) / pol.length,
  });
  return puntoEnPoligono(centro(a), b) || puntoEnPoligono(centro(b), a);
}

/** ¿El segmento ab toca el polígono (cruza un borde o tiene un extremo dentro)? */
export function segmentoTocaPoligono(a: Punto, b: Punto, poligono: readonly Punto[]): boolean {
  if (puntoEnPoligono(a, poligono) || puntoEnPoligono(b, poligono)) return true;
  for (let i = 0; i < poligono.length; i++) {
    if (intersectarSegmentos(a, b, poligono[i]!, poligono[(i + 1) % poligono.length]!)) return true;
  }
  return false;
}

/** Ángulo equivalente en (−π, π], sin ruido numérico acumulado por giros sucesivos. */
export function normalizarAngulo(r: number): number {
  const dos = 2 * Math.PI;
  let x = ((r % dos) + dos) % dos;
  if (x > Math.PI + 1e-9) x -= dos;
  return Math.round(x * 1e12) / 1e12 || 0;
}

/** Giro (como rotación de un elemento) cuyo frente apunta en la dirección `d`. */
export function giroHacia(d: Punto): number {
  return normalizarAngulo(Math.atan2(d.x, d.z));
}

/** Reduce un polígono hacia su centro `m` metros (para tolerancias). */
export function encoger(poligono: readonly Punto[], m: number): Punto[] {
  const cx = poligono.reduce((s, q) => s + q.x, 0) / poligono.length;
  const cz = poligono.reduce((s, q) => s + q.z, 0) / poligono.length;
  return poligono.map((q) => {
    const dx = q.x - cx;
    const dz = q.z - cz;
    const l = Math.hypot(dx, dz) || 1;
    const k = Math.max(0, l - m) / l;
    return { x: cx + dx * k, z: cz + dz * k };
  });
}
